import { AxiosResponse } from 'axios'
import { toJS } from 'mobx'
import { userIsLoggedIn } from '../components/core/Authentication'
import { MediaTypes, MediaTypesType } from '../components/core/Utils'
import { useAuth } from '../hooks/useAuth'

/** Returns the proper favorite (or unfavorite) function for a corresponding mediaType and arpMediaIndex */
const useFavorite = (arpMediaIndex: number, mediaType: MediaTypesType) => {
  const { authenticatedPost, authenticatedDelete, setUser, user } = useAuth()
  // In the case of genesis songs, we aren't actually dealing with the
  // arpMediaIndex but instead the genesis token index
  const tokenIndex = arpMediaIndex

  let isFavorited: boolean = false
  if (userIsLoggedIn(user)) {
    switch (mediaType) {
      case MediaTypes.SONG:
        isFavorited = user.favorite_songs && user.favorite_songs.includes(arpMediaIndex)
        break
      case MediaTypes.STEM:
        isFavorited = user.favorite_stems && user.favorite_stems.includes(arpMediaIndex)
        break
      case MediaTypes.SAMPLE:
        isFavorited = user.favorite_samples && user.favorite_samples.includes(arpMediaIndex)
        break
      case MediaTypes.GENESISSONG:
        isFavorited =
          user.favorite_genesis_songs && user.favorite_genesis_songs.includes(Number(tokenIndex))
        break
    }
  }

  //////////////////////// API Functions //////////////////////////////////

  // Favorite

  const favoriteMediaForWallet = async (
    arpMediaIndex: number,
    type: MediaTypesType
  ): Promise<any> => {
    let resp: AxiosResponse
    if (type === MediaTypes.GENESISSONG) {
      resp = await authenticatedPost(`/api/v1/song-genesis/favorite`, {
        song_id: tokenIndex
      })
    } else {
      resp = await authenticatedPost(`/api/v2/arp-media/${arpMediaIndex}/favorites`)
    }
    return resp.data
  }

  // Unfavorite

  const unfavoriteMediaForWallet = async (arpMediaIndex: number, type: MediaTypesType) => {
    let resp: AxiosResponse
    if (type === MediaTypes.GENESISSONG) {
      resp = await authenticatedPost(`/api/v1/song-genesis/unfavorite`, {
        song_id: tokenIndex
      })
    } else {
      resp = await authenticatedDelete(`/api/v2/arp-media/${arpMediaIndex}/favorites`)
    }
    return resp.data
  }

  //////////////////////// End API Functions //////////////////////////////

  /////////////// Helper Functions /////////////////

  // Add to Favorites

  const addMediaToUserFavorites = (arpMediaIndex: number, type: MediaTypesType) => {
    if (!user) return

    const userCopy = toJS(user)
    switch (type) {
      case MediaTypes.SONG:
        userCopy.favorite_songs
          ? userCopy.favorite_songs.push(arpMediaIndex)
          : (userCopy.favorite_songs = [arpMediaIndex])
        break
      case MediaTypes.STEM:
        userCopy.favorite_stems
          ? userCopy.favorite_stems.push(arpMediaIndex)
          : (userCopy.favorite_stems = [arpMediaIndex])
        break
      case MediaTypes.SAMPLE:
        userCopy.favorite_samples
          ? userCopy.favorite_samples.push(arpMediaIndex)
          : (userCopy.favorite_samples = [arpMediaIndex])
        break
      case MediaTypes.GENESISSONG:
        userCopy.favorite_genesis_songs
          ? userCopy.favorite_genesis_songs.push(tokenIndex)
          : (userCopy.favorite_genesis_songs = [tokenIndex])
        break
    }
    setUser(userCopy)
  }

  // Remove from Favorites

  const removeMediaFromUserFavorites = (arpMediaIndex: number, type: MediaTypesType) => {
    if (!user) return

    const userCopy = toJS(user)
    switch (type) {
      case MediaTypes.SONG:
        if (!user.favorite_songs) {
          return
        }
        userCopy.favorite_songs = userCopy.favorite_songs.filter((index) => index !== arpMediaIndex)
        break
      case MediaTypes.STEM:
        if (!user.favorite_stems) {
          return
        }
        userCopy.favorite_stems = userCopy.favorite_stems.filter((index) => index !== arpMediaIndex)
        break
      case MediaTypes.SAMPLE:
        if (!user.favorite_samples) {
          return
        }
        userCopy.favorite_samples = userCopy.favorite_samples.filter(
          (index) => index !== arpMediaIndex
        )
        break
      case MediaTypes.GENESISSONG:
        if (!user.favorite_genesis_songs) {
          return
        }
        userCopy.favorite_genesis_songs = userCopy.favorite_genesis_songs.filter(
          (index) => index !== tokenIndex
        )
        break
    }

    setUser(userCopy)
  }

  function segmentTrackButton(index: number, arg1: string) {
    throw new Error('Function not implemented.')
  }

  ////////////////////////// End helper functions ///////////////

  // Determine the correct favorite/unfavorite function based on the media type
  // TODO: Add back in segment tracking
  const toggleFavorite = () => {
    isFavorited
      ? unfavoriteMediaForWallet(arpMediaIndex, mediaType).then(() =>
          removeMediaFromUserFavorites(arpMediaIndex, mediaType)
        )
      : favoriteMediaForWallet(arpMediaIndex, mediaType).then(() =>
          addMediaToUserFavorites(arpMediaIndex, mediaType)
        )
  }

  const segmentTrackFavorite = async () => {
    let button: string
    if (isFavorited) {
      button = 'Unfavorite'
    } else button = 'Favorite'
    segmentTrackButton(arpMediaIndex, 'Button Clicked')
  }

  return { toggleFavorite, isFavorited }
}

export default useFavorite
